import React from "react";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DATE_RANGE_SHORTCUTS_QUARTERS} from "./DateRangeShortcuts.ts";
import type {DateRange, PickersShortcutsProps} from "@mui/x-date-pickers-pro";
import {DateRangePicker, useIsValidValue, usePickerContext} from "@mui/x-date-pickers-pro";
import {useStores} from "../../stores";
import {getDayJsLocale} from "../../utils/day-js-utils.ts";
import type {PickerRangeValue} from "@mui/x-date-pickers/internals";
import type {Dayjs} from "dayjs";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import {Chip, Divider, ListItem} from "@mui/material";

declare type Props = {
    /** Current value */
    value: PickerRangeValue
    /** Click handler for applying the updates from DateRangePicker. */
    onChange: (value: PickerRangeValue) => void,
    /** Do not allow to open of popover */
    disabled?: boolean
}

/** The DateRangePicker component allows a user to select a range of dates */
export const DateRangePickerMui: React.FC<Props> = ({value, onChange, disabled}) => {
    const {p} = useStores();
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={getDayJsLocale(p.p.locale)}>
            <DateRangePicker
                disabled={disabled}
                onChange={onChange}
                value={value}
                calendars={2}
                slots={{
                    shortcuts: CustomRangeShortcuts,
                }}
                slotProps={{
                    shortcuts: {
                        items: DATE_RANGE_SHORTCUTS_QUARTERS,
                        // changeImportance: 'set', // testing
                    },
                    textField: {
                        fullWidth: true,
                    },
                    toolbar: {
                        hidden: false,
                    },
                    actionBar: {
                        actions: ['clear'],
                    },
                    // calendarHeader: {}
                }}
            />
        </LocalizationProvider>
    )
}

const CustomRangeShortcuts: React.FC<PickersShortcutsProps<DateRange<Dayjs>>> = ({
                                                                                     items,
                                                                                     changeImportance = 'accept'
                                                                                 }) => {
    const isValid = useIsValidValue<DateRange<Dayjs>>();
    const {setValue} = usePickerContext<DateRange<Dayjs>>();

    if (items == null || items.length === 0) {
        return null;
    }

    const resolvedItems = items.map((item) => {
        const newValue = item.getValue({isValid});

        return {
            label: item.label,
            onClick: () => {
                setValue(newValue, {changeImportance, shortcut: item});
            },
            disabled: !isValid(newValue),
        };
    });

    return (
        <Box sx={{gridRow: 2, gridColumn: 1}}>
            <List
                dense
                sx={(theme) => ({
                    px: theme.spacing(1),
                    '& .MuiListItem-root': {
                        px: 0,
                        py: theme.spacing(0.25),
                    },
                })}
            >
                {resolvedItems.map((item, index, array) =>
                    <ListItem key={item.label}>
                        <Chip {...item} variant={index === array.length - 1 ? 'outlined' : 'filled'}/>
                    </ListItem>
                )}
            </List>
            <Divider/>
        </Box>
    );
}
